import { ContentCard } from "@msuite/ui/content-card";
import { DataGrid } from "@msuite/ui/data-grid";
import { icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import type { IncomingInvoiceDetail, Nullable } from "@msuite/utils/types";
import {
	formatInvoiceAmount,
	IncomingInvoiceDocumentPreviewButton,
	formatInvoiceDate,
	reminderLevelLabel,
} from "@/components/incoming-invoices";

/** Props Interface */
interface IncomingInvoiceRemindersCardProps {
	incomingInvoice: Nullable<IncomingInvoiceDetail>;
}

/** The supplier's reminders of the invoice, the latest first; nothing while there are none. */
export function IncomingInvoiceRemindersCard({
	incomingInvoice,
}: IncomingInvoiceRemindersCardProps) {
	/** Context */
	const { t } = useTranslation();

	/** Computed */
	const reminders = incomingInvoice?.reminders ?? [];

	/** Guard */
	if (!reminders.length) return null;

	/** Render */
	return (
		<ContentCard
			title={t("incoming_invoices.reminders.reminders")}
			icon={icon("BellRing")}
		>
			<DataGrid>
				{reminders.map((reminder) => (
					<DataGrid.Row key={reminder.id}>
						<DataGrid.Label>{t(reminderLevelLabel(reminder.level))}</DataGrid.Label>
						<DataGrid.Value
							append={
								reminder.fileId && <IncomingInvoiceDocumentPreviewButton fileId={reminder.fileId} />
							}
						>
							{t("incoming_invoices.reminders.summary", {
								date: formatInvoiceDate(reminder.reminderDate),
								dueDate: formatInvoiceDate(reminder.dueDate),
								fee: formatInvoiceAmount(reminder.fee ?? 0, reminder.currency),
							})}
						</DataGrid.Value>
					</DataGrid.Row>
				))}
			</DataGrid>
		</ContentCard>
	);
}
